import { message } from 'antd';
import stringConverter from 'utils/stringConverter';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import jwt from './jwt';

interface ErrorProps {}

message.config({
    maxCount: 5,
});

function useHandleError() {
    const intl = useIntl();
    const { toSnake } = stringConverter();
    const navigate = useNavigate();

    const handleError: any = (
        error: any,
        key?: string,
        specialMessage?: string,
        specialAttribute?: string,
        dontShowMessages?: boolean
    ) => {
        if (error?.response?.data?.code && error?.response?.data?.code === 401 && error?.response?.data?.error === 'Unauthorized') {
            navigate('/login');
            jwt.remove();
        }

        // Services can attach contacts to a business error (e.g. the learning managers to
        // reach about a missing licence) — show them together with the message.
        const contacts: string[] = Array.isArray(error?.learningManagerEmails)
            ? error.learningManagerEmails.filter(Boolean)
            : [];

        const withContacts = (text: string) => (contacts.length ? `${text} ${contacts.join(', ')}` : text);

    // Services return either a translation key ('validation.some_key', 'already_initiated') or a
    // ready-made sentence. Length is not a reliable way to tell them apart — a key-shaped string is.
    const isTranslationKey = (text: string) => /^[a-z0-9_]+(\.[a-z0-9_]+)*$/.test(text);

    // Resolve a server-supplied error string to displayable text. Prose used to be replaced by the
    // generic "data load error" toast purely for being long, which hid real validation messages.
    const resolveErrorText = (text: string): string => {
        if (isTranslationKey(text)) {
            return intl.formatMessage({ id: text, defaultMessage: text });
        }

        // Some services answer with HTTP 200 wrapping an error body, which reaches us as the JSON
        // string '{"error":"...","code":422}'. Unwrap it so the actual reason is shown instead of
        // the generic toast the raw blob would otherwise fall through to.
        const trimmed = text.trim();

        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            try {
                const parsed = JSON.parse(trimmed);

                if (typeof parsed?.error === 'string') {
                    return resolveErrorText(parsed.error);
                }

                if (typeof parsed?.message === 'string') {
                    return resolveErrorText(parsed.message);
                }

                // Laravel's validator bag, JSON-encoded into `error`:
                // '{"description":["The description field is required."]}'. Surface the messages
                // themselves — this is the shape that previously produced a bare "Data load error".
                if (parsed && typeof parsed === 'object') {
                    const fieldMessages = Object.values(parsed)
                        .flatMap((value: any) => (Array.isArray(value) ? value : [value]))
                        .filter((value: any) => typeof value === 'string' && value.trim().length);

                    if (fieldMessages.length) {
                        return fieldMessages.join(' ');
                    }
                }
            } catch {
                // Not JSON after all — fall through to the plain-text handling below.
            }
        }

        const isSingleLinePlainText = !/[\r\n<>{}]/.test(text) && text.length <= 200;

        return isSingleLinePlainText ? text : intl.formatMessage({ id: 'error.data_load' });
    };

        if (!dontShowMessages) {
            if (specialMessage) {
                message.error(intl.formatMessage({ id: specialMessage }, { attribute: specialAttribute }), 15);
            } else if (error.hasOwnProperty('message') && typeof error.message === 'string') {
                if (error.message === 'dont_have_permission_to_access') {
                    message.error(intl.formatMessage({ id: error.message }), 15);
                    navigate('/');
                } else {
                    message.error(withContacts(resolveErrorText(error.message)), 15);
                }
            } else if (typeof error?.error === 'string') {
                // Direct service responses keep the key in `error`: {error: 'validation.some_key', code: 4xx}
                message.error(withContacts(resolveErrorText(error.error)), 15);
            } else if (!!key) {
                Object.entries(error).forEach((entry: any) => {
                    const convertedString = toSnake(entry[0]);
                    const attribute = intl.formatMessage({ id: key + '.' + convertedString });

                    message.error(intl.formatMessage({ id: entry[1][0] }, { attribute: attribute }), 15);
                });
            } else {
                message.error(intl.formatMessage({ id: 'error.data_load' }), 15);
            }
        }

        document.body.scrollTop = document.documentElement.scrollTop = 0; //pure js scroll to top, supported in all browsers
        console.error(error);
    };

    const prepareFormValidation: any = (error: any, key?: string) => {
        let errorList: any = [];

        if (typeof error?.error !== 'string') {
            Object.entries(error).forEach((entry: any) => {
                const convertedString = toSnake(entry[0]);
                const attribute = intl.formatMessage({ id: key + '.' + convertedString });

                const errorObject = {
                    errors: [intl.formatMessage({ id: entry[1][0] }, { attribute: attribute })],
                    name: entry[0],
                };

                errorList.push(errorObject);
            });
        }

        return errorList;
    };

    return [handleError, prepareFormValidation];
}

export default useHandleError;
